import { doRequest } from '@r1-frontend/do-request';
import getPackageIdBySubscriptionIds
    from '@r1-frontend/api-domru/api-tariffs/v1/tariffs/[salePackageId]/packageIdBySubscriptions';
import {
    IOfferCreateResponseDto,
    offerCreate,
} from '@r1-frontend/api-domru/full-buy/v2/agreement/offer-create';

import { getGaId } from '@r1-frontend/shared/helpers/getGaId';

import FullBuyAnalytics from '~/src/features/full-buy/analytics';
import {
    calculateTotalPrices,
    getErrorMessage,
    isGigabitCanConnected,
    isGigaSpeedRequired,
    isTariffCanConnected,
    submitRequest,
} from '~/src/features/full-buy/functions';
import { buildProductsForRequest } from '~/src/features/full-buy/helpers';
import { setCalculatedSalePackageId, setPromoCode } from '~/src/features/full-buy/store/actions';
import { goToNextStep } from '~/src/features/full-buy/store/actions/goToNextStep';
import { getAgreementDataString } from '~/src/features/full-buy/store/actions/helpers';
import { setStep } from '~/src/features/full-buy/store/actions/setStep';
import { createOfferRequestBody, getSummaryCheckInfo } from '~/src/features/full-buy/store/functions';
import { selectDataForCreateAgreement } from '~/src/features/full-buy/store/selectors/selectDataForCreateAgreement';
import { TDispatch, TGetState } from '~/src/store';
import { selectCsrf } from '~/src/store/selectors/auth/selectCsrf';

import { setAgreementData } from './index';

/**
 * Создание договора
 * TODO Рефакторинг функции, перенести второй запрос, подготовка дынных в отдельную функцию
 */
export const createAgreement = (analytics: FullBuyAnalytics) => {
    return async(dispatch: TDispatch, getState: TGetState): Promise<void> => {
        const state = getState();
        const {
            tariffPackage,
            isPhoneConfirmed,
            providerId,
            promoCode,
            isHouseConnected,
            gigabitAvail,
            products,
            fio,
            phone,
            flow,
            selectedSubscriptions,

            addressParams,
            unifiedSelectedAddress,
        } = selectDataForCreateAgreement(state);
        const csrf = selectCsrf(state);

        if (!tariffPackage || !(addressParams || unifiedSelectedAddress) || !isPhoneConfirmed || !fio || !phone || !flow) {
            throw new Error('FullBuy Error. Required data is not set');
        }

        const lastStepKey = flow.steps.length - 1;

        let calculatedSalePackageId: number | null = null;

        if (selectedSubscriptions && selectedSubscriptions.length > 0) {
            const response = await doRequest.apiTariffs<{
                salePackageId: number,
            }>(getPackageIdBySubscriptionIds(tariffPackage.sale_package_id, selectedSubscriptions));

            if (response.isSuccess) {
                calculatedSalePackageId = Number(response.payload.salePackageId);
                dispatch(setCalculatedSalePackageId(calculatedSalePackageId));
            }
        }

        const { monthlyPayment, oncePayment } = calculateTotalPrices(
            tariffPackage.promoPrice || tariffPackage.price,
            tariffPackage.connectionPrice.final,
            products,
        );

        const summaryCheckInfo = getSummaryCheckInfo({
            promoPrice: tariffPackage.promoPrice,
            price: tariffPackage.price,
            products: products,
            connectPrice: tariffPackage.connectionPrice.final,
            oncePaymentSum: oncePayment,
            monthlyPaymentSum: monthlyPayment,
        });
        const tariffId = calculatedSalePackageId
            ? calculatedSalePackageId
            : tariffPackage.sale_package_id;

        const offerRequestBody = createOfferRequestBody(
            addressParams,
            unifiedSelectedAddress,
            {
                fio,
                phone,
                tariffId,
                productsByProps: buildProductsForRequest([...products, ...tariffPackage.additionalProducts]),
                summaryCheckInfo,
                promoCode,
                analytics_id: getGaId(),
            },
        );

        const respAgreement = await doRequest.fullBuy<IOfferCreateResponseDto>(
            offerCreate(providerId, offerRequestBody),
        );

        analytics.createAgreement.try();

        const agreementDataString = getAgreementDataString(tariffPackage.tariff_name, monthlyPayment, oncePayment);

        /* Ошибка при создании договора. Таймслоты пропускаем переходим к финальному шагу */
        if (!respAgreement.isSuccess) {
            const gigabitMayConnect = isGigabitCanConnected(gigabitAvail);
            const requiredGigaSpeed = isGigaSpeedRequired(tariffPackage.speed);
            const shouldConnect = isTariffCanConnected(isHouseConnected, requiredGigaSpeed, gigabitMayConnect);

            submitRequest({
                isMono: !!tariffPackage.isMono,
                fio,
                phone,
                csrf,
                sale_package_id: tariffPackage.sale_package_id,
                tariff_name: tariffPackage.tariff_name,
                additionalProducts: tariffPackage.additionalProducts,
                products: products,
                shouldConnect,
                address: unifiedSelectedAddress?.address.value,
                street: addressParams?.street.label,
                house: addressParams?.house.label,
                flat: addressParams?.flat,
            });

            dispatch(setAgreementData({
                agreementId: null,
                agreementNumber: null,
                isDouble: false,
                hasError: true,
                errorMessage: getErrorMessage({}),
            }));

            // TODO вызывать nextStep - логику пропуска шага таймслотов реализовать в контейнере EngineerRequest
            // Добавить тест на проверку шага
            dispatch(setStep(lastStepKey));

            analytics.createAgreement.fail(respAgreement.error?.message, agreementDataString);

            if (promoCode) {
                analytics.createAgreement.promocodeActivation(`error | ${promoCode}`);
            }

            return;
        }

        const { isDouble, fromCache, plbRegistrationSuccessful, promoCodeSuccessful } = respAgreement.payload;
        const agreementNumber = respAgreement.isSuccess ? Number(respAgreement.payload.agreementNumber) : null;
        const agreementId = respAgreement.isSuccess ? Number(respAgreement.payload.agreementId) : null;

        /* Создан двойной договор. Таймслоты пропускаем переходим к финальному шагу */
        if (isDouble || fromCache) {
            // TODO рефакторинг, не обрабатывается ситуация если fromCache = true, уточнить flow для fromCache = true
            dispatch(setAgreementData({
                agreementId: null,
                agreementNumber: null,
                isDouble,
                hasError: true,
                errorMessage: getErrorMessage({ isDouble, isFromCache: fromCache }),
            }));

            // TODO вызывать nextStep - логику пропуска шага таймслотов реализовать в контейнере EngineerRequest
            dispatch(setStep(lastStepKey));

            analytics.createAgreement.double(agreementNumber, agreementDataString);
            return;
        }

        if (!agreementId) {
            throw new Error('FullBuy Error. Required agreementId!');
        }

        if (!agreementNumber) {
            throw new Error('FullBuy Error. Required agreementNumber!');
        }

        /* Договор создан без ошибок, переходим к тайм-слотам */
        dispatch(setAgreementData({
            agreementId,
            agreementNumber,
            isDouble: false,
            hasError: false,
            errorMessage: '',
        }));

        // @ts-ignore
        dispatch(goToNextStep());

        analytics.createAgreement.success(agreementNumber, agreementDataString);

        if (promoCode) {
            // Проверяем, зарегистрирован ли промокод
            if (plbRegistrationSuccessful || promoCodeSuccessful) {
                analytics.createAgreement.promocodeActivation(`success | ${promoCode}`);
            } else {
                // Ошибка
                analytics.createAgreement.promocodeActivation(`error | ${promoCode}`);
                dispatch(setPromoCode({ value: promoCode, hasError: true }));
            }
        }

        return;
    };
};
